/**
 * Getting Started widget for new users
 */
import { useOnboardingProgress } from '../../hooks'
import type { OnboardingStep } from '../../hooks/useOnboardingProgress'

export function GettingStartedWidget() {
  const { steps, percentComplete, isDismissed, isLoading, dismiss } = useOnboardingProgress({ autoEnroll: true })

  // Don't show if dismissed (user explicitly hid it)
  // Note: We show even when 100% complete if user clicked "Show Again"
  if (isDismissed) {
    return null
  }

  // Don't show loading state - just hide until ready
  if (isLoading) {
    return null
  }

  const completedCount = steps.filter(s => s.isComplete).length

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Getting Started</h2>
          <p className="text-sm text-gray-500 mt-1">
            Complete these steps to set up your portfolio tracker
          </p>
        </div>
        <button
          onClick={dismiss}
          className="text-gray-400 hover:text-gray-600 p-1"
          title="Dismiss"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>{completedCount} of {steps.length} completed</span>
          <span>{percentComplete}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${percentComplete}%` }}
          />
        </div>
      </div>

      {/* Steps checklist */}
      <div className="mt-4 space-y-3">
        {steps.map((step) => (
          <StepItem key={step.id} step={step} />
        ))}
      </div>
    </div>
  )
}

function StepItem({ step }: { step: OnboardingStep }) {
  return (
    <a
      href={step.href}
      className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
        step.isComplete
          ? 'bg-green-50 cursor-default'
          : 'bg-gray-50 hover:bg-gray-100'
      }`}
    >
      {/* Checkbox icon */}
      <div
        className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
          step.isComplete
            ? 'bg-green-500 text-white'
            : 'border-2 border-gray-300'
        }`}
      >
        {step.isComplete && (
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium ${
            step.isComplete ? 'text-green-800 line-through' : 'text-gray-900'
          }`}
        >
          {step.title}
        </p>
        <p
          className={`text-sm ${
            step.isComplete ? 'text-green-600' : 'text-gray-500'
          }`}
        >
          {step.description}
        </p>
      </div>

      {/* Arrow for incomplete steps */}
      {!step.isComplete && (
        <svg
          className="flex-shrink-0 w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </a>
  )
}
